import { datadogRum } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';
import { datadogLogs } from '@datadog/browser-logs';
import { isRecoverableDomMutationError, shouldDiscardRumErrorEvent } from './domErrors';

interface DatadogUser {
  id: string;
  email?: string;
  role?: string;
}

type DatadogContextValue = string | number | boolean | undefined;

type DatadogContext = Record<string, DatadogContextValue>;

export const DATADOG_EVENT = {
  apiCall: 'api_call',
  cabinetTableFilterChanged: 'cabinet_table_filter_changed',
  creativeSelected: 'creative_selected',
  createFacebookAdSuccess: 'create_facebook_ad_success',
  facebookFormRestart: 'facebook_form_restart',
  facebookFormStepChange: 'facebook_form_step_change',
  facebookFormSubmitAttempt: 'facebook_form_submit_attempt',
  tikTokFormRestart: 'tiktok_form_restart',
  tikTokFormStepChange: 'tiktok_form_step_change',
  tikTokFormSubmitAttempt: 'tiktok_form_submit_attempt',
  createTikTokAdSuccess: 'create_tiktok_ad_success',
  twitterFormRestart: 'twitter_form_restart',
  twitterFormStepChange: 'twitter_form_step_change',
  twitterFormSubmitAttempt: 'twitter_form_submit_attempt',
  createTwitterAdSuccess: 'create_twitter_ad_success',
  pinterestFormRestart: 'pinterest_form_restart',
  pinterestFormStepChange: 'pinterest_form_step_change',
  pinterestFormSubmitAttempt: 'pinterest_form_submit_attempt',
  createPinterestAdSuccess: 'create_pinterest_ad_success',
  facebookPackAdded: 'facebook_pack_added',
  facebookPackSplit: 'facebook_pack_split',
  packCreated: 'pack_created',
  packDeleted: 'pack_deleted',
  packUndo: 'pack_undo',
} as const;

const DEFAULT_ENV =
  window.location.hostname.includes('-stage.asqq.io')
    ? 'stage' :
    window.location.hostname.includes('-dev.asqq.io')
      ? 'dev' :
      'production';

const DATADOG_ENABLED = process.env.NODE_ENV !== 'test' && process.env.REACT_APP_DATADOG_ENABLED !== 'false';

let isDatadogInitialized = false;

const trackDatadogAction = (
  name: string,
  context?: DatadogContext,
) => {
  if (!isDatadogInitialized) {
    return;
  }

  datadogRum.addAction(name, context);
};

const initDatadogRum = () => {
  if (
    DEFAULT_ENV !== 'production' ||
    isDatadogInitialized ||
    !DATADOG_ENABLED ||
    !process.env.REACT_APP_DATADOG_APPLICATION_ID ||
    !process.env.REACT_APP_DATADOG_CLIENT_TOKEN
  ) {
    return;
  }

  const initConfigs = {
    applicationId: process.env.REACT_APP_DATADOG_APPLICATION_ID,
    clientToken: process.env.REACT_APP_DATADOG_CLIENT_TOKEN,
    site: process.env.REACT_APP_DATADOG_SITE,
    service: process.env.REACT_APP_DATADOG_SERVICE,
    env: DEFAULT_ENV,
    version: '1.0.0',
  }

  datadogRum.init({
    ...initConfigs,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 20,
    trackResources: true,
    trackUserInteractions: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'allow',
    plugins: [reactPlugin({ router: true })],
    beforeSend: (event) => !shouldDiscardRumErrorEvent(event),
  });

  datadogLogs.init({
    ...initConfigs,
    sessionSampleRate: 100,
    forwardErrorsToLogs: false,
    forwardConsoleLogs: [],
    forwardReports: [],
  });

  isDatadogInitialized = true;
};

const setDatadogUser = ({ id, email, role }: DatadogUser) => {
  if (!isDatadogInitialized) {
    return;
  }

  datadogRum.setUser({
    id,
    email,
    role,
  });
  datadogLogs.setUser({
    id,
    email,
    role,
  });
};

const trackDatadogError = (
  error: Error | string,
  context?: DatadogContext,
) => {
  if (!isDatadogInitialized || isRecoverableDomMutationError(error)) {
    return;
  }

  datadogRum.addError(error, context);

  datadogLogs.logger.error(
    typeof error === 'string' ? error : error.message,
    {
      ...context,
      errorName: typeof error === 'string' ? 'Error' : error.name,
      errorMessage: typeof error === 'string' ? error : error.message,
      errorStack: typeof error === 'string' ? undefined : error.stack,
      trackedBy: 'trackDatadogError',
    },
    error instanceof Error ? error : undefined
  );
};

const datadogActions = {
  initDatadogRum,
  setDatadogUser,
  trackDatadogAction,
  trackDatadogError,
};

export default datadogActions;
export * from './cabinetTracking';
export {
  isRecoverableDomMutationError,
  isRecoverableDomMutationMessage,
  shouldDiscardRumErrorEvent,
} from './domErrors';
